import { z } from 'zod'
import { ResultsWaitSchema } from '../domain/results-wait'
import {
  CouncilStatusSchema,
  DecryptionStateSchema,
  ElectionKeyModeSchema,
  ElectionStatusSchema,
  JobKindSchema,
  JobStatusSchema,
  ProcessPhaseSchema,
} from '../enums'
import {
  DEFAULT_RESIDENCE_DECLARATION_DAYS,
  MIN_SCHEDULED_START_LEAD_SECONDS,
  ResidenceDeclarationDaysSchema,
  ScheduleSchema,
} from '../domain/election-config'
import { QuestionInputSchema } from '../domain/questions'
import { CouncilPolicySchema } from './councils'
import {
  BigIntStringSchema,
  EthAddressSchema,
  HexSchema,
  IsoDateTimeSchema,
  ProcessIdSchema,
  SlugSchema,
  UuidSchema,
} from '../primitives'

/**
 * Votings ("elections" in the code and the database) belong to an organization (PLAN v3 §1, §4). Organizer routes:
 * `/api/org/organizations/:orgId/elections[/:electionId]`. The member community, KYC and Didit configuration live on
 * the organization; a voting only has its questions, schedule and eligibility extras.
 */

export const MAX_QUESTIONS = 20

/* ================================================================== voting body (draft) */

/**
 * Everything the organizer authors (voting wizard: basics → questions → eligibility & extras → schedule → review).
 * Drafts are saved with lenient validation; `publishIssues()` lists what still blocks publishing (the server runs
 * the same function, plus the organization checks, on POST /publish and answers 400 validation_error).
 */
export const ElectionBodySchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(200),
  description: z.string().trim().max(10_000).default(''),
  questions: z.array(QuestionInputSchema).max(MAX_QUESTIONS).default([]),
  schedule: ScheduleSchema.prefault({}),
  /**
   * District filter: only active members whose CURRENT district is listed may vote (S8, checked live at the CSP).
   * Empty = every member. Must be district names of the organization's districting list (publish check).
   */
  eligibleDistricts: z.array(z.string().trim().min(1).max(200)).max(1000).default([]),
  /** Residence declaration checkbox on the ballot ("resided at least N days"); null = not asked. */
  residenceDeclarationDays: ResidenceDeclarationDaysSchema.default(DEFAULT_RESIDENCE_DECLARATION_DAYS),
  /**
   * Email every eligible member an invitation with the voting link when the voting OPENS (wizard toggle, default ON).
   * Sent once (M3); members activated while it is open get it at activation (S9).
   */
  notifyMembers: z.boolean().default(true),
  /**
   * Who can unlock the results (wizard Schedule step, "Who can unlock the results?"): `dkg` = Automatic (the DAVINCI
   * key network, default), `council` = an election committee of the organization (`councilId` required).
   * `sequencer` is never offered (server gate, see ELECTION_KEY_MODES). Frozen at publish.
   */
  keyMode: ElectionKeyModeSchema.default('dkg'),
  /** The organization's committee (councils row id) when `keyMode` is `council`; ignored (stored null) otherwise. */
  councilId: UuidSchema.nullable().default(null),
})
export type ElectionBody = z.infer<typeof ElectionBodySchema>
export type ElectionBodyDraft = z.input<typeof ElectionBodySchema>

/* POST /api/org/organizations/:orgId/elections → 201 ElectionResponse (status draft). */
export const ElectionCreateRequestSchema = ElectionBodySchema
export type ElectionCreateRequest = z.input<typeof ElectionCreateRequestSchema>

/**
 * PATCH /api/org/organizations/:orgId/elections/:electionId → ElectionResponse.
 * Any subset; arrays replace the stored value. While `locks.structural` is true only `title` / `description`
 * (pushed on chain with updateMetadata, until the start: `locks.metadata`) and `notifyMembers` (until the
 * invitations are enqueued) are accepted;
 * re-sending a locked field unchanged is fine; otherwise 409 election_not_editable (details.fields). `keyMode` and
 * `councilId` are frozen from publish on (`locks.keyMode`), whatever the structural lock says.
 */
export const ElectionUpdateRequestSchema = z
  .object({
    title: ElectionBodySchema.shape.title,
    description: z.string().trim().max(10_000),
    questions: z.array(QuestionInputSchema).max(MAX_QUESTIONS),
    schedule: ScheduleSchema,
    eligibleDistricts: z.array(z.string().trim().min(1).max(200)).max(1000),
    residenceDeclarationDays: ResidenceDeclarationDaysSchema,
    notifyMembers: z.boolean(),
    /** Draft only: frozen once published (409 election_not_editable `fields: ['keyMode']`). */
    keyMode: ElectionKeyModeSchema,
    /** Draft only: frozen once published (409 election_not_editable `fields: ['councilId']`). */
    councilId: UuidSchema.nullable(),
  })
  .partial()
export type ElectionUpdateRequest = z.input<typeof ElectionUpdateRequestSchema>

export interface PublishIssue {
  path: (string | number)[]
  message: string
}

/**
 * What still blocks publishing a voting draft. Empty array ⇒ publishable. `organizationDistricts` (district names
 * of the organization, see `districtNames()`) enables the eligible-district check.
 */
export function publishIssues(
  body: ElectionBody,
  now: Date = new Date(),
  organizationDistricts?: readonly string[]
): PublishIssue[] {
  const issues: PublishIssue[] = []
  if (body.questions.length === 0) issues.push({ path: ['questions'], message: 'Add at least one question' })
  const s = body.schedule
  if (s.startMode === 'scheduled' && s.startAt) {
    if (Date.parse(s.startAt) - now.getTime() < MIN_SCHEDULED_START_LEAD_SECONDS * 1000)
      issues.push({
        path: ['schedule', 'startAt'],
        message: 'A scheduled start must be at least 10 minutes from now (or use a manual start)',
      })
  }
  if (s.endMode === 'scheduled' && s.endAt) {
    const start = s.startMode === 'scheduled' && s.startAt ? Date.parse(s.startAt) : now.getTime()
    if (Date.parse(s.endAt) <= start) issues.push({ path: ['schedule', 'endAt'], message: 'End must be after start' })
  }
  if (body.keyMode === 'council' && !body.councilId)
    issues.push({ path: ['councilId'], message: 'Choose the election committee that will unlock the results' })
  if (organizationDistricts) {
    const unknown = body.eligibleDistricts.filter((d) => !organizationDistricts.includes(d))
    if (unknown.length)
      issues.push({
        path: ['eligibleDistricts'],
        message: `Unknown district${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}`,
      })
  }
  return issues
}

/* ================================================================== stored voting */

/**
 * Where an ended process's results stand (process sync: davinci-sdk `getResultsStatus`, plus the committee's partial
 * progress for `council` processes). Absent before the end.
 */
export const ProcessDecryptionSchema = z.object({
  state: DecryptionStateSchema,
  /** When `state` was first observed (stall detection: Automatic > 30 min, committee > 24 h, finalizable > 2 min). */
  stateSince: IsoDateTimeSchema,
  /** End of the grace window (results cannot be published before). */
  graceEnd: IsoDateTimeSchema.nullable(),
  /** `awaiting-opening` only: how the committee's decryption opens (SDK `decryptionOpening`). */
  opening: z
    .object({
      mode: z.enum(['scheduled', 'manual']),
      /** Scheduled: the opening date; manual: the fallback date, or null without one. */
      opensAt: IsoDateTimeSchema.nullable(),
    })
    .nullable(),
  /** Committee members who turned their key for this process (council, after the opening). */
  partialsDone: z.number().int().min(0).nullable(),
  /** t of the committee (council). */
  threshold: z.number().int().min(1).nullable(),
  /** n of the committee (council). */
  size: z.number().int().min(1).nullable(),
  /** When the committee's organizer opened decryption (manual mode), if it did. */
  openedAt: IsoDateTimeSchema.nullable(),
  updatedAt: IsoDateTimeSchema,
})
export type ProcessDecryption = z.infer<typeof ProcessDecryptionSchema>

/** Per-question DAVINCI process state, kept fresh by the backend sync worker. */
export const ProcessStateSchema = z.object({
  phase: ProcessPhaseSchema,
  processId: ProcessIdSchema.nullable(),
  createTxHash: HexSchema.nullable(),
  startDate: IsoDateTimeSchema.nullable(),
  endDate: IsoDateTimeSchema.nullable(),
  votersCount: z.number().int().min(0).nullable(),
  overwrittenVotesCount: z.number().int().min(0).nullable(),
  lastSyncAt: IsoDateTimeSchema.nullable(),
  error: z.string().nullable(),
  /** Results progress once voting ended (see ProcessDecryptionSchema); absent / null before. */
  decryption: ProcessDecryptionSchema.nullable().optional(),
  /** Address that created the process (the organization's creator wallet; the treasury for legacy processes). */
  createdBy: EthAddressSchema.nullable().optional(),
})
export type ProcessState = z.infer<typeof ProcessStateSchema>

/** Decoded tally of one question (SDK `decodeResults`). Totals are decimal strings (bigint). */
export const QuestionResultsSchema = z.object({
  /** SDK BallotKind: the preset type or `custom`. */
  kind: z.string(),
  /** Ballots counted (latest ballot of each voter). */
  voters: z.number().int().min(0),
  choices: z.array(
    z.object({
      index: z.number().int().min(0),
      title: z.string(),
      total: BigIntStringSchema,
      /** total / voters (share, mean rating, mean rank — lower preferred, mean votes). */
      mean: z.number().nullable(),
    })
  ),
  /** true once the on-chain results are set (phase `results`). */
  final: z.boolean(),
  updatedAt: IsoDateTimeSchema,
})
export type QuestionResults = z.infer<typeof QuestionResultsSchema>

export const QuestionMetaSchema = z.object({
  id: UuidSchema,
  position: z.number().int().min(0),
  process: ProcessStateSchema,
  results: QuestionResultsSchema.nullable(),
})

/** Stored question = authored question + ids/process/results. Narrow on `type` as usual. */
export const QuestionSchema = z.intersection(QuestionInputSchema, QuestionMetaSchema)
export type Question = z.infer<typeof QuestionSchema>

export const JobSummarySchema = z.object({
  id: z.number().int(),
  kind: JobKindSchema,
  status: JobStatusSchema,
  attempts: z.number().int().min(0),
  lastError: z.string().nullable(),
  runAfter: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})
export type JobSummary = z.infer<typeof JobSummarySchema>

/** Lifecycle actions the organizer can trigger right now (drives the dashboard buttons). */
export const ElectionActionsSchema = z.object({
  canEdit: z.boolean(),
  /** Draft AND the organization is published. */
  canPublish: z.boolean(),
  canStart: z.boolean(),
  canEnd: z.boolean(),
  canExtend: z.boolean(),
  canCancel: z.boolean(),
  canRetry: z.boolean(),
  canDelete: z.boolean(),
})
export type ElectionActions = z.infer<typeof ElectionActionsSchema>

/** Delivery counts of a communication / invitation (derived from its recipient rows). */
export const DeliveryCountsSchema = z.object({
  total: z.number().int().min(0),
  pending: z.number().int().min(0),
  sent: z.number().int().min(0),
  failed: z.number().int().min(0),
  skipped: z.number().int().min(0),
})
export type DeliveryCounts = z.infer<typeof DeliveryCountsSchema>

/** Invitation email of a voting (shown on the voting overview). */
export const InvitationStatusSchema = z.object({
  /** = notifyMembers */
  enabled: z.boolean(),
  /** Set once (compare-and-set, M3) by whichever path first observes the OPEN transition. */
  enqueuedAt: IsoDateTimeSchema.nullable(),
  /** The `invitation` communication holding the recipients (null before enqueue). */
  communicationId: UuidSchema.nullable(),
  counts: DeliveryCountsSchema.nullable(),
})
export type InvitationStatus = z.infer<typeof InvitationStatusSchema>

/** The election committee a voting is bound to (its key holders), as shown to the organizer. */
export const ElectionCommitteeSchema = z.object({
  id: UuidSchema,
  /** Ceremony id (bytes12 hex). */
  cid: z.string(),
  label: z.string(),
  status: CouncilStatusSchema,
  threshold: z.number().int().min(1).nullable(),
  size: z.number().int().min(1).nullable(),
  policy: CouncilPolicySchema.nullable(),
  /** `${COUNCIL_APP_URL}/c/${cid}`: where the committee's organizer opens the results. */
  appUrl: z.string().nullable(),
})
export type ElectionCommittee = z.infer<typeof ElectionCommitteeSchema>

export const ElectionSchema = z.object({
  id: UuidSchema,
  /** Unique within the organization; generated from the title. */
  slug: SlugSchema,
  organizationId: UuidSchema,
  title: z.string(),
  description: z.string(),
  status: ElectionStatusSchema,
  questions: z.array(QuestionSchema),
  schedule: ScheduleSchema,
  eligibleDistricts: z.array(z.string()),
  residenceDeclarationDays: z.number().int().nullable(),
  notifyMembers: z.boolean(),
  invitations: InvitationStatusSchema,
  /** Who can unlock the results (frozen at publish). */
  keyMode: ElectionKeyModeSchema,
  /** The committee (councils row) when keyMode is `council`. */
  councilId: UuidSchema.nullable(),
  committee: ElectionCommitteeSchema.nullable(),
  /** Inherited from the organization (read-only here): "[TEST]" on chain, TEST badges. */
  testMode: z.boolean(),
  /** `${PUBLIC_URL}/o/${orgSlug}/v/${slug}` (voting page; invitation links). */
  publicUrl: z.string(),
  cspAddress: EthAddressSchema.nullable(),
  /**
   * H3: structural fields locked once any DAVINCI process exists. `keyMode`: the key choice (keyMode + councilId)
   * is frozen from publish on.
   */
  locks: z.object({
    structural: z.boolean(),
    reason: z.string().nullable(),
    keyMode: z.boolean(),
    /** title / description frozen: a process has started (the registry refuses the metadata update). */
    metadata: z.boolean(),
  }),
  actions: ElectionActionsSchema,
  /** Latest lifecycle job (create/end/cancel/extend), if any. */
  job: JobSummarySchema.nullable(),
  /** Last lifecycle error shown to the organizer (e.g. start_failed reason). */
  lastError: z.string().nullable(),
  publishedAt: IsoDateTimeSchema.nullable(),
  openedAt: IsoDateTimeSchema.nullable(),
  endedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})
export type Election = z.infer<typeof ElectionSchema>

export const ElectionResponseSchema = z.object({ election: ElectionSchema })
export type ElectionResponse = z.infer<typeof ElectionResponseSchema>

/* GET /api/org/organizations/:orgId/elections → ElectionListResponse (newest first). */
export const ElectionSummarySchema = z.object({
  id: UuidSchema,
  slug: SlugSchema,
  title: z.string(),
  status: ElectionStatusSchema,
  schedule: ScheduleSchema,
  keyMode: ElectionKeyModeSchema,
  /** Election committee votings after the end: the results wait for the committee (not "counting"); else null. */
  resultsWait: ResultsWaitSchema.nullable(),
  testMode: z.boolean(),
  questionsCount: z.number().int().min(0),
  eligibleDistricts: z.array(z.string()),
  publicUrl: z.string(),
  counts: z.object({
    /** Members eligible right now (memberEligibility, live). */
    eligibleMembers: z.number().int().min(0),
    /** Distinct members with at least one recorded vote. */
    voters: z.number().int().min(0),
  }),
  invitationsEnqueuedAt: IsoDateTimeSchema.nullable(),
  openedAt: IsoDateTimeSchema.nullable(),
  endedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})
export type ElectionSummary = z.infer<typeof ElectionSummarySchema>

export const ElectionListResponseSchema = z.object({ elections: z.array(ElectionSummarySchema) })
export type ElectionListResponse = z.infer<typeof ElectionListResponseSchema>

/* ================================================================== lifecycle */

/*
 * POST …/elections/:electionId/publish  (draft → published; needs a published organization (409
 *                                         invalid_state_transition otherwise); scheduled start ⇒ queues
 *                                         create_processes)
 * POST …/elections/:electionId/start    (published + manual start → starting; queues create_processes)
 * POST …/elections/:electionId/retry    (start_failed → starting; resumes, skipping questions with a processId)
 * POST …/elections/:electionId/end      (open → closing; closeProcessIn for every question)
 * POST …/elections/:electionId/cancel   (any non-final → canceled; cancels created processes)
 * POST …/elections/:electionId/extend   (open with scheduled/duration end → new end)
 * DELETE …/elections/:electionId        (draft only) → OkResponse
 * All lifecycle POSTs answer ElectionResponse; 409 invalid_state_transition otherwise.
 * The OPEN transition (job handler or process sync) enqueues the invitations once (M3) when notifyMembers.
 */
export const EndElectionRequestSchema = z.object({
  /** Notice before the end, minutes (≥ the registry's noticeMin; server clamps). */
  noticeMinutes: z
    .number()
    .int()
    .min(1)
    .max(24 * 60)
    .optional(),
})
export type EndElectionRequest = z.infer<typeof EndElectionRequestSchema>

export const ExtendElectionRequestSchema = z
  .object({ endAt: IsoDateTimeSchema.optional(), addSeconds: z.number().int().min(60).optional() })
  .refine((v) => (v.endAt ? 1 : 0) + (v.addSeconds ? 1 : 0) === 1, 'Provide exactly one of endAt or addSeconds')
export type ExtendElectionRequest = z.infer<typeof ExtendElectionRequestSchema>

/* ================================================================== voting dashboard */

/* GET /api/org/organizations/:orgId/elections/:electionId/dashboard */
export const ElectionDashboardResponseSchema = z.object({
  election: ElectionSchema,
  kpis: z.object({
    /** Members eligible right now (live: status, district filter, age at the voting start). */
    eligibleMembers: z.number().int().min(0),
    /** Distinct members with at least one recorded vote. */
    voters: z.number().int().min(0),
    /** voters / eligibleMembers (0..1), null when nobody is eligible. */
    turnout: z.number().min(0).nullable(),
    votesByQuestion: z.array(
      z.object({
        questionId: UuidSchema,
        title: z.string(),
        /** Ballot submissions recorded by our API (incl. revotes). */
        recordedVotes: z.number().int().min(0),
        /** Distinct members that voted on this question. */
        recordedVoters: z.number().int().min(0),
        /** votersCount from the sequencer / chain (null before sync). */
        onChainVoters: z.number().int().min(0).nullable(),
      })
    ),
  }),
  /** Ballots recorded per day (UTC), oldest first. */
  votesByDay: z.array(z.object({ day: z.string(), count: z.number().int().min(0) })),
  sequencer: z.object({ url: z.string(), reachable: z.boolean(), checkedAt: IsoDateTimeSchema.nullable() }),
})
export type ElectionDashboardResponse = z.infer<typeof ElectionDashboardResponseSchema>

/* ================================================================== results */

/* GET …/elections/:electionId/results (also embedded in the public voting once it has results) */
export const ResultsResponseSchema = z.object({
  electionId: UuidSchema,
  status: ElectionStatusSchema,
  keyMode: ElectionKeyModeSchema,
  questions: z.array(
    z.object({
      questionId: UuidSchema,
      position: z.number().int().min(0),
      title: z.string(),
      type: z.string(),
      options: z.array(z.object({ title: z.string() })),
      processId: ProcessIdSchema.nullable(),
      phase: ProcessPhaseSchema,
      decryption: ProcessDecryptionSchema.nullable(),
      results: QuestionResultsSchema.nullable(),
    })
  ),
})
export type ResultsResponse = z.infer<typeof ResultsResponseSchema>
